import { POPPY_REACT_FLOW_NODE_TYPES } from '@poppy/utils';
import type { CreatorProfilePlatformType } from '@poppy/firebase';
import { Bot, Unplug } from 'lucide-react';
import { ModelId, DEFAULT_MODEL_ID } from '@poppy/models';
import { previewServiceOrigin } from './previewServiceOrigin';

export const IS_SERVER = typeof window === 'undefined';

// Default AI model to use when no model is specified or model is invalid
export const DEFAULT_AI_MODEL_ID: ModelId = DEFAULT_MODEL_ID;

if (!process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY) {
  throw new Error('Missing Env: [NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY] is requried but not provided');
}

export const ENV = {
  UPLOAD_CARE_PUBLIC_KEY: process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY,
} as const;

export const PANEL_TYPES = {
  VERSION_CONTROL: 'version-control',
  NOTES: 'notes',
};

export const ALLOWED_MIME_TYPES = [
  // Document types (our own conversion API reads all of them)
  'application/pdf; charset=utf-8',
  'application/pdf',
  'text/plain',
  'text/markdown', // .md
  'text/x-markdown', // .md (alternative MIME type)
  'text/csv',
  'application/rtf', // .rtf
  'text/rtf', // .rtf (alternative MIME type)
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.oasis.opendocument.text', // .odt
  'application/vnd.oasis.opendocument.spreadsheet', // .ods
  'application/vnd.oasis.opendocument.presentation', // .odp
  'application/epub+zip', // .epub
  'application/x-mobipocket-ebook', // .mobi
  // Image types
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/gif',
  // Video types
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime', // .mov
  'video/x-msvideo', // .avi
  'video/x-ms-wmv', // .wmv
  'video/x-flv', // .flv
  'video/x-matroska', // .mkv
  // Audio types
  'audio/mpeg', // .mp3 (standard MIME type)
  'audio/mp3', // .mp3 (non-standard but sometimes used)
  'audio/wav',
  'audio/ogg',
  'audio/flac',
  'audio/mp4', // .m4a
  'audio/x-m4a', // .m4a (alternative MIME type)
  'audio/m4a', // .m4a (alternative MIME type)
  'audio/aac', // .aac
  'audio/x-caf', // .caf
  'audio/caf',
];

// How big each node starts: kept with the rest of what is known about node types.
export { NODE_DIMENSIONS } from '@poppy/utils';

/** Audio node height when transcript is expanded (max) */
export const AUDIO_NODE_EXPANDED_HEIGHT = 320;
/** Height of header + player + notes + padding (node without transcript content) */
export const AUDIO_NODE_BASE_HEIGHT = 100;
/** Max height allocated to transcript area when expanded */
export const AUDIO_NODE_TRANSCRIPT_MAX_HEIGHT = 220;

export const MIDDLEWARE_PATHNAME_HEADER = 'x-pathname';

/**
 * Determines the appropriate API URL based on the environment and origin
 * @param originUrl - The origin URL of the request
 * @returns The API URL to use
 */
export const getApiUrl = (originUrl: string): string => {
  const isDevelopment = originUrl.includes('localhost');
  const previewApi = previewServiceOrigin(originUrl, 'poppyai-api');
  if (previewApi) return previewApi;

  if (isDevelopment) {
    return 'http://localhost:3001';
  }

  return 'https://api.getpoppy.ai';
};

/**
 * Default height of a creator profile node, per platform.
 *
 * Sized for the two-row layout, which is what a multi-view selection renders:
 * chrome above (header, profile, both selects), two rows of a label, the tile
 * and a scrollbar, then the freshness footer. LinkedIn comes out slightly
 * shorter than the other two because its row is two wider content-height cards
 * rather than three fixed 9:16 crops.
 *
 * One value per platform, not per selection. A node that resized itself when
 * you changed a dropdown would shove its neighbours around the board; a single
 * view keeps the wrapping grid and simply shows more of it.
 *
 * Also the resize cap (see CreatorProfileNode), so the default and the maximum
 * cannot drift apart. Any tuning has to hold the width still: the grid tiles
 * are `1fr`, so row height follows the node's width.
 */
export const CREATOR_PROFILE_NODE_HEIGHT: Record<CreatorProfilePlatformType, number> = {
  instagram: 676,
  tiktok: 676,
  linkedin: 671,
};

/**
 * Determines the appropriate chatbot URL based on the environment and origin
 * @param originUrl - The origin URL of the request
 * @returns The chatbot URL to use
 */
export const getChatbotUrl = () => {
  if (typeof window === 'undefined') {
    return 'http://localhost:3002';
  }

  const originUrl = window.location.origin;
  const isDevelopment = originUrl.includes('localhost');
  const previewChatbot = previewServiceOrigin(originUrl, 'poppyai-chatbot');
  if (previewChatbot) return previewChatbot;

  if (isDevelopment) {
    return 'http://localhost:3002';
  }

  return 'https://chat.getpoppy.ai';
};

export const SERVICES_CONNECTOR_OPTIONS = {
  CHAT_BOT: {
    id: 'chatBot',
    title: 'Chatbot',
    description: 'Connect to a chatbot',
    icon: '/chatBotServices.svg',
  },
};

export const SERVICES_CONNECTOR_TOOLBAR_OPTIONS_V2 = {
  CHAT_BOT: {
    id: 'chatBot',
    analyticsName: 'chatbot',
    title: 'Chatbot',
    description: 'Connect to a chatbot',
    icon: Bot,
    canAccess: (roles: string[] | undefined | null) => true,
  },
  API: {
    id: 'api',
    analyticsName: 'api',
    title: 'API',
    description: 'Connect to an API',
    icon: Unplug,
    canAccess: (roles: string[] | undefined | null) => true,
  },
};

export const SOCIAL_BOTS =
  /(twitterbot|facebookexternalhit|linkedinbot|slack|discordbot|whatsapp|telegrambot|pinterest|vkshare|embedly|skypeuripreview|bitlybot|redditbot)/i;
